import React, { useState, useEffect, useContext, useRef } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Alert 
} from '@mui/material';
import { 
  McIcon, 
  McButton,
  McTable,
  McTabBar,
  McTab,
  McTypeahead,
  McModal,
  McInput,
  McBadge,
  McMenu,
  McList,
  McListItem,
  McTag,
  McSelect,
  McOption
} from '@maersk-global/mds-react-wrapper';
import { McCheckbox } from '@maersk-global/mds-react-wrapper/components-core/mc-checkbox';
import { McLoadingIndicator } from "@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator";
import { TableContext } from "../../context";
import { toast } from 'react-toastify';
import axios from "axios";
import * as XLSX from 'xlsx';
import { Skeleton } from '@mui/material';
import FilterMenu from '../FilterMenu';

const RapidHealthCheck = ({ 
  formData, 
  updateFormData, 
  appOnboardingData,
  setAppOnboardingData,
  onConfigurationChange
}) => {
  // Define the Maersk font family
  const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';
  
  // State for repository data and UI
  const [relatedRepos, setRelatedRepos] = useState(null);
  const [suggestedRepos, setSuggestedRepos] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("To related_repositories");
  const [deletedRows, setDeletedRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [selectedApplicationInstance, setSelectedApplicationInstance] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [isFilterMenuOpened, setIsFilterMenuOpened] = useState(false);
  const [serverName, setServerName] = useState("");
  const [repoSearch, setRepoSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const typeaheadref = useRef(null);
  const additionalData = [];
  
  // Add this with other state declarations at the top of the component
  const [isNewRelationModalOpened, setIsNewRelationModalOpened] = useState(false);
  const [isGatekeeperModalOpened, setIsGatekeeperModalOpened] = useState(false);
  const [gatekeeperStatus, setGatekeeperStatus] = useState('pending'); // 'pending', 'pass', 'fail'
  const [gatekeeperMessage, setGatekeeperMessage] = useState('');
  const [isGatekeeperLoading, setIsGatekeeperLoading] = useState(false);
  const [msaCertificationStatus, setMsaCertificationStatus] = useState('pending'); // 'pending', 'pass', 'fail'
  const [msaCertificationMessage, setMsaCertificationMessage] = useState('');
  const [msaCertificationPercentage, setMsaCertificationPercentage] = useState(0);
  const [isMsaCertificationLoading, setIsMsaCertificationLoading] = useState(false);
  const { graphData, showInstanceSelector, selectedInstance } =
    useContext(TableContext);

  // Column mappings for repositories (similar to AppToRepoTableContainer)
  const repoColumns = [
    { id: 'ApplicationId', label: 'Application ID', sortable: true },
    { id: 'ApplicationName', label: 'Application Instance', sortable: true },
    { id: 'nameWithOwner', label: 'Repository Name', sortable: true },
    { id: 'lastCommitAuthor', label: 'Last Commit Author', sortable: true },
    { id: 'primaryLanguage', label: 'Primary Language', sortable: true },
    { id: 'url', label: 'Repository URL', sortable: true },
    { id: 'status', label: 'Status', sortable: true }
  ];

  const suggestedRepoColumns = [
    { id: 'nameWithOwner', label: 'Repository Name', sortable: true },
    { id: 'lastCommitAuthor', label: 'Last Commit Author', sortable: true },
    { id: 'primaryLanguage', label: 'Primary Language', sortable: true },
    { id: 'url', label: 'Repository URL', sortable: true }
  ];

  // Add this with other handlers in the component
  const handleNewRelation = () => {
    setIsNewRelationModalOpened(true);
  };

  const handleGatekeeperIntegration = () => {
    setIsGatekeeperModalOpened(true);
  };

  const handleRunMsaCertificationCheck = async () => {
    try {
      setIsMsaCertificationLoading(true);
      setMsaCertificationStatus('pending');
      setMsaCertificationMessage('Checking MSA certification...');
      
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const payload = {
        applicationId: appOnboardingData?.application_id,
        application_instances: appOnboardingData?.onboarding_details?.application_instances
      };

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/msa_certification_check`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 200 && result.certification_percentage !== undefined) {
          setMsaCertificationPercentage(result.certification_percentage);
          
          if (result.certification_percentage === 100) {
            setMsaCertificationStatus('pass');
            setMsaCertificationMessage('All application instances are fully certified!');
          } else {
            setMsaCertificationStatus('fail');
            setMsaCertificationMessage(result.message || `Certification incomplete: ${result.certification_percentage}%`);
          }
        } else {
          console.error("Unexpected response format:", result);
          setMsaCertificationStatus('fail');
          setMsaCertificationMessage(result.message || 'Unexpected response format');
        }
      } else {
        const result = response.data || {};
        console.error("Failed to check MSA certification - HTTP status:", response.status);
        setMsaCertificationStatus('fail');
        setMsaCertificationMessage(result.message || `HTTP error: ${response.status}`);
      }
    } catch (error) {
      console.error("Error checking MSA certification:", error);
      setMsaCertificationStatus('fail');
      setMsaCertificationMessage(error.message || 'Failed to check MSA certification');
      toast.error("Failed to check MSA certification", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } finally {
      setIsMsaCertificationLoading(false);
    }
  };

  const handleRunGatekeeperCheck = async () => {
    try {
      setIsGatekeeperLoading(true);
      setGatekeeperStatus('pending');
      setGatekeeperMessage('Checking repositories...');
      
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      // Get all repositories from the related repos
      const repositories = relatedRepos ? relatedRepos.map(repo => repo.nameWithOwner || repo.name) : [];
      
      if (repositories.length === 0) {
        setGatekeeperStatus('fail');
        setGatekeeperMessage('No repositories found to check');
        return;
      }

      const payload = {
        application_id: appOnboardingData?.application_id,
        repository_names: repositories
      };

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/gatekeeper_onboarding_check`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 200 && result.data) {
          const matchedRepos = result.data.matched_repositories || [];
          const totalRepos = repositories.length;
          const matchedCount = matchedRepos.length;
          
          // Check if all repositories are found in matched_repositories
          const allReposFound = repositories.every(repo => 
            matchedRepos.some(matched => matched.includes(repo) || repo.includes(matched))
          );
          
          setGatekeeperStatus(allReposFound ? 'pass' : 'fail');
          setGatekeeperMessage(`Found ${matchedCount} out of ${totalRepos} repositories in Gatekeeper`);
          
          if (allReposFound) {
            toast.success("All repositories found in Gatekeeper integration", {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light"
            });
          } else {
            toast.error(`Only ${matchedCount}/${totalRepos} repositories found in Gatekeeper`, {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light"
            });
          }
        } else {
          console.error("Unexpected response format:", result);
          setGatekeeperStatus('fail');
          setGatekeeperMessage(result.message || 'Unexpected response format');
        }
      } else {
        // Handle different HTTP status codes
        const result = response.data || {};
        
        if (response.status === 404 && result.message?.includes("No matching records found")) {
          // This is a valid response - no repositories found in Gatekeeper
          setGatekeeperStatus('fail');
          setGatekeeperMessage(`Found 0 out of ${repositories.length} repositories in Gatekeeper`);
        } else {
          // This is an actual error
          console.error("Failed to check Gatekeeper integration - HTTP status:", response.status);
          setGatekeeperStatus('fail');
          setGatekeeperMessage(result.message || `HTTP error: ${response.status}`);
        }
      }
    } catch (error) {
      console.error("Error checking Gatekeeper integration:", error);
      setGatekeeperStatus('fail');
      setGatekeeperMessage(error.message || 'Failed to check integration');
    } finally {
      setIsGatekeeperLoading(false);
    }
  };

  const handleServerNameChange = (e) => {
    setServerName(e.target.value);
  };

  const handleClearButtonClick = (e) => {
    setServerName("");
  };

  // Get endpoint for tab
  const getEndpointForTab = (tabLabel) => {
    if (tabLabel === "To related_repositories") {
      return "/get_related_repos";
    } else if (tabLabel === "To suggested_repositories") {
      return "/get_suggested_repos";
    }
    return "";
  };

  // Fetch repository data
  const fetchRepositoryData = async (tabLabel, appId, appInstances) => {
    setIsLoading(true);

    try {
      const endpoint = getEndpointForTab(tabLabel);
      const onboardingToken = localStorage.getItem('azure_token');
      const payload = {
        applicationId: appId,
        application_instances: appInstances,
        username: graphData?.displayName,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change${endpoint}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      const responseData = response.data;

      // Handle new API response format
      if (responseData.status === 200 && responseData.data) {
        if (tabLabel === "To related_repositories") {
          // Transform related repositories data
          const relatedRepos = responseData.data.related_repositories || {};
          const transformedData = [];
          
          Object.keys(relatedRepos).forEach(instanceName => {
            const instanceData = relatedRepos[instanceName];
            if (instanceData.repositories && Array.isArray(instanceData.repositories)) {
              instanceData.repositories.forEach(repo => {
                transformedData.push({
                  ...repo,
                  // Map new field names to old field names for backward compatibility
                  nameWithOwner: repo.repository_name,
                  url: repo.repository_url,
                  primaryLanguage: repo.primary_language,
                  lastCommitAuthor: repo.last_commit_author,
                  ApplicationId: responseData.data.application_id,
                  ApplicationName: instanceName,
                  id: repo._id || `${responseData.data.application_id}-${instanceName}-${repo.repository_name}`
                });
              });
            }
          });
          
          setRelatedRepos(transformedData);
        } else if (tabLabel === "To suggested_repositories") {
          // Transform suggested repositories data
          const suggestedRepos = responseData.data.suggested_repositories || {};
          const transformedData = [];
          
          Object.keys(suggestedRepos).forEach(instanceName => {
            const instanceData = suggestedRepos[instanceName];
            if (instanceData.suggestions && Array.isArray(instanceData.suggestions)) {
              instanceData.suggestions.forEach(repo => {
                transformedData.push({
                  ...repo,
                  // Map new field names to old field names for backward compatibility
                  nameWithOwner: repo.repository_name,
                  url: repo.repository_url,
                  primaryLanguage: repo.primary_language,
                  lastCommitAuthor: repo.last_commit_author,
                  ApplicationId: responseData.data.application_id,
                  ApplicationName: instanceName,
                  id: repo._id || `${responseData.data.application_id}-${instanceName}-${repo.repository_name}`
                });
              });
            }
          });
          
          setSuggestedRepos(transformedData);
        }
      } else if ((!responseData.data || typeof responseData.data !== "object")) {
        console.warn(`Invalid data received for ${tabLabel}:`, responseData.data);
        if (tabLabel === "To related_repositories") {
          setRelatedRepos([]);
        } else {
          setSuggestedRepos([]);
        }
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to fetch repository data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      console.error(`Error fetching repository data for ${tabLabel}:`, error);
      if (tabLabel === "To related_repositories") {
        setRelatedRepos([]);
      } else {
        setSuggestedRepos([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Search repositories
  const handleSearch = async (e) => {
    const onboardingToken = localStorage.getItem('azure_token');
    if (e.target.value.length < 3) {
      return;
    }
    const query = e.target.value;
    try {
      const payload = {
        repoName: query
      };

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/getallreposlist`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      const responseData = response.data;
      const processedOptions = (responseData.suggestions || []).map((option) => ({
        label: option.label?.replace("Maersk-Global/", "") || option.label,
        value: option.value?.replace("Maersk-Global/", "") || option.value
      }));
      setOptions(processedOptions); // setOptions(null)
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Error occurred while fetching suggestions", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    }
  };

  const handleOptionSelected = (e) => {
    setRepoSearch(e.detail.label);
  };

  const handleRepoNameSearch = async (e) => {
    const label = activeTab;
    const appName = appOnboardingData?.onboarding_details?.application_instances[0];
    
    if (activeTab === "To related_repositories") {
      setRelatedRepos(null);
    } else {
      setSuggestedRepos(null);
    }

    try {
      const payload = {
        repositoryName: repoSearch,
        applicationName: appName,
        tabIndex: activeTab === "To related_repositories" ? 0 : 1,
        user_name: graphData?.displayName
      };

      const response = await axios.post(
        `${process.env.REACT_APP_CMDB_BACKEND_URI}/getrepodata`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("cmdb_token")}`
          }
        }
      );

      const responseData = response.data;
      const data = responseData.suggested_data;
      
      if (activeTab === "To related_repositories") {
        setRelatedRepos(data);
      } else {
        setSuggestedRepos(data);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Error occurred while fetching repository data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    }
  };

  const combinedData = React.useMemo(() => {
    const data = activeTab === "To related_repositories" ? relatedRepos : suggestedRepos;
    if (!data) return [];
    return [
      // Put additionalData first
      ...additionalData.map((item, index) => ({
        ...item,
        id: `secondary-${item.id || index}`
      })),
      // Then add the primary data
      ...data.map((item, index) => ({
        ...item,
        id: item.id || `primary-${index}`
      }))
    ];
  }, [relatedRepos, suggestedRepos, activeTab, additionalData]);

  const filteredData = React.useMemo(() => {
    if (!activeFilters || activeFilters.length === 0) {
      return combinedData;
    }

    // Use the filter logic from FilterMenu component
    return combinedData.filter((item) => {
      return activeFilters.every((filter) => {
        if (!filter.field || !filter.operator || !filter.value) return true;

        const itemValue = item[filter.field];
        const filterValue = filter.value.toLowerCase();

        if (itemValue === undefined || itemValue === null) return false;

        // Handle both string and array data types
        const processItemValue = (value) => {
          if (Array.isArray(value)) {
            return value.map((v) => String(v).toLowerCase());
          }
          return String(value).toLowerCase();
        };

        const processedItemValue = processItemValue(itemValue);

        switch (filter.operator) {
          case "contains":
            if (Array.isArray(processedItemValue)) {
              return processedItemValue.some(v => v.includes(filterValue));
            }
            return processedItemValue.includes(filterValue);

          case "Does not contain":
            if (Array.isArray(processedItemValue)) {
              return !processedItemValue.some(v => v.includes(filterValue));
            }
            return !processedItemValue.includes(filterValue);

          case "equals":
            if (Array.isArray(processedItemValue)) {
              return processedItemValue.includes(filterValue);
            }
            return processedItemValue === filterValue;

          case "Is not":
            if (Array.isArray(processedItemValue)) {
              return !processedItemValue.includes(filterValue);
            }
            return processedItemValue !== filterValue;

          case "startsWith":
            if (Array.isArray(processedItemValue)) {
              return processedItemValue.some(v => v.startsWith(filterValue));
            }
            return processedItemValue.startsWith(filterValue);

          case "endsWith":
            if (Array.isArray(processedItemValue)) {
              return processedItemValue.some(v => v.endsWith(filterValue));
            }
            return processedItemValue.endsWith(filterValue);

          case "is one of":
            const filterValues = filterValue.split(',').map(v => v.trim().toLowerCase());
            if (Array.isArray(processedItemValue)) {
              return processedItemValue.some(v => filterValues.includes(v));
            }
            return filterValues.includes(processedItemValue);

          case "is not one of":
            const filterValuesNot = filterValue.split(',').map(v => v.trim().toLowerCase());
            if (Array.isArray(processedItemValue)) {
              return !processedItemValue.some(v => filterValuesNot.includes(v));
            }
            return !filterValuesNot.includes(processedItemValue);

          default:
            return true;
        }
      });
    });
  }, [combinedData, activeFilters]);

  const [tableStyles, setTableStyles] = useState(`
    .mds-table {
      max-height: 50vh !important;
    }
    ${combinedData
      .filter((row) => row.id.startsWith("primary-"))
      .map(
        (row) => `
        .mds-table .row-${row.id}  td {
          border-left: 4px solid var(--mds_brand_appearance_primary_background-color);
        }
      `
      )
      .join("\n")}
    ${combinedData
      .filter((row) => row.id.startsWith("secondary-"))
      .map(
        (row) => `
        .mds-table .row-${row.id}  td {
          border-left: 4px solid var(--mds_brand_appearance_warning_background-color);
          background-color: var(--mds_brand_appearance_error_subtle_background-color) !important;
        text-decoration: line-through;
        }
      `
      )
      .join("\n")}
    .mds-table .row-secondary-sec-003  td {
        background-color: var(--mds_brand_appearance_error_subtle_background-color) !important;
        text-decoration: line-through;
      }
    
  `);

  const handleCertify = async (row) => {
    setLoading(true);
    try {
      const payload = {
        applicationName: selectedApplicationInstance || appOnboardingData?.application_name,
        applicationId: appOnboardingData?.application_id,
        repositoryNames: row.map((r) => r.nameWithOwner).join(", "),
        user_name: graphData?.displayName
      };

      const response = await axios.post(
        `${process.env.REACT_APP_CMDB_BACKEND_URI}/createrepomapping`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("cmdb_token")}`
          }
        }
      );

      toast.success(`Successfully tagged repositories to ${selectedApplicationInstance || appOnboardingData?.application_name}`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      
      // Reset MSA certification status since repository configuration has changed
      setMsaCertificationStatus('pending');
      setMsaCertificationMessage('');
      setMsaCertificationPercentage(0);
    } catch (error) {
      console.error("Error certifying row:", error);
      toast.error("Error occurred while certifying rows", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } finally {
      await Promise.all([
        fetchRepositoryData("To suggested_repositories", appOnboardingData?.application_id, appOnboardingData?.onboarding_details?.application_instances),
        fetchRepositoryData("To related_repositories", appOnboardingData?.application_id, appOnboardingData?.onboarding_details?.application_instances)
      ]);
      setLoading(false);
      setIsDeleteModalOpened(false);
      setSelectedRows([]);
      setSelectedApplicationInstance(''); // Reset the selected instance
    }
  };

  const handleDownload = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data available to download", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      return;
    }

    try {
      const columnNameMapping = {
        ApplicationId: "Application ID",
        ApplicationName: "Application Name",
        nameWithOwner: "Repository Name",
        primaryLanguage: "Primary Language",
        lastCommitAuthor: "Last Commit Author",
        url: "Repository URL",
        status: "Status"
      };
      // Create a clean dataset for export by removing internal properties
      const exportData = filteredData.map((row) => {
        const cleanRow = { ...row };
        // Remove internal properties that shouldn't be exported
        delete cleanRow.id;
        delete cleanRow.selectDisabled;
        delete cleanRow.staging;
        delete cleanRow.last_updated_by;
        delete cleanRow._id;
        delete cleanRow.created_by;
        delete cleanRow.embedding;

        const mappedRow = {};
        Object.keys(cleanRow).forEach((key) => {
          const mappedKey = columnNameMapping[key] || key; // Use mapped name or fallback to original name
          mappedRow[mappedKey] = cleanRow[key];
        });

        return mappedRow;
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Auto-size columns
      const columnWidths = [];

      // Get column headers from the columns prop or from the first row
      const headers = getCurrentColumns()?.map((col) => col.label) || Object.keys(exportData[0] || {});

      headers.forEach((header, index) => {
        const maxLength = Math.max(header.length, ...exportData.map((row) => String(row[header] || "").length));
        columnWidths[index] = { width: Math.min(maxLength + 2, 50) };
      });

      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      const createSheetName = (appName, labelName) => {
        const cleanAppName = (appName || "App").replace(/[\\\/\?\*\[\]]/g, ""); // Remove invalid characters
        const cleanLabel = (labelName || "Data").replace(/[\\\/\?\*\[\]]/g, "");

        // Try different combinations to fit within 31 chars
        let sheetName = `${cleanAppName}-${cleanLabel}`;

        if (sheetName.length <= 31) {
          return sheetName;
        }

        // If too long, try abbreviating
        const maxAppLength = 31 - cleanLabel.length - 1; // -1 for the dash
        if (maxAppLength > 0) {
          sheetName = `${cleanAppName.substring(0, maxAppLength)}-${cleanLabel}`;
          if (sheetName.length <= 31) {
            return sheetName;
          }
        }

        // If still too long, use just label or truncate further
        if (cleanLabel.length <= 31) {
          return cleanLabel;
        }

        // Last resort: truncate to 31 chars
        return sheetName.substring(0, 31);
      };

      const sheetName = createSheetName(appOnboardingData?.application_name, activeTab);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `${appOnboardingData?.application_name || "Application"}_${activeTab || "Data"}_${timestamp}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);

      toast.success(`Downloaded ${filteredData.length} records successfully`, {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } catch (error) {
      console.error("Error downloading data:", error);
      toast.error("Error occurred while downloading data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    }
  };

  const handleDelete = async (row) => {
    setLoading(true);
    try {
      const payload = {
        applicationName: appOnboardingData?.application_name,
        repositoryNames: row.map((r) => r.nameWithOwner),
        user_name: graphData?.displayName
      };

      const response = await axios.post(
        `${process.env.REACT_APP_CMDB_BACKEND_URI}/deleterepomapping`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("cmdb_token")}`
          }
        }
      );

      toast.success("Successfully deleted rows", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      
      // Reset MSA certification status since repository configuration has changed
      setMsaCertificationStatus('pending');
      setMsaCertificationMessage('');
      setMsaCertificationPercentage(0);
    } catch (error) {
      console.error("Error certifying row:", error);
      toast.error("Error occurred while deleting rows", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } finally {
      await fetchRepositoryData(activeTab, appOnboardingData?.application_id, appOnboardingData?.onboarding_details?.application_instances);
      setLoading(false);
      setIsDeleteModalOpened(false);
      setSelectedRows([]);
    }
  };

  useEffect(() => {
    // console.log("tableStyles updated", tableStyles);
  }, [tableStyles]);

  // Update localStorage when selectedRows changes

  const handleSelectChange = (e) => {
    const newSelection = e.detail || [];

    // Avoid unnecessary state updates if selections are identical
    const isSame =
      newSelection.length === selectedRows.length && newSelection.every((row, idx) => row.id === selectedRows[idx]?.id);

    if (isSame) return;
    setSelectedRows(newSelection);
  };

  const handleRowSelected = (e) => {
    // console.log("Row selected", e.detail);
  };

  // Handle tab change
  const handleTabChange = (e) => {
    const tabIndex = e.detail;
    const newTab = tabIndex === 0 ? "To related_repositories" : "To suggested_repositories";
    setActiveTab(newTab);
    // Clear selected rows when switching tabs
    setSelectedRows([]);
    // Clear form data
    updateFormData('githubRepos', ['']);
  };

  // Get current data for active tab
  const getCurrentData = () => {
    return filteredData;
  };

  // Get current columns for active tab
  const getCurrentColumns = () => {
    return activeTab === "To related_repositories" ? repoColumns : suggestedRepoColumns;
  };

  // Fetch data on component mount
  useEffect(() => {
    if (appOnboardingData?.application_id) {
      fetchRepositoryData("To related_repositories", appOnboardingData?.application_id, appOnboardingData?.onboarding_details?.application_instances);
      fetchRepositoryData("To suggested_repositories", appOnboardingData?.application_id, appOnboardingData?.onboarding_details?.application_instances);
    }
  }, [appOnboardingData?.application_id]);

  const currentData = getCurrentData();
  const currentColumns = getCurrentColumns();
  const isConfigured = relatedRepos && relatedRepos.length > 0 && msaCertificationStatus === 'pass' && msaCertificationPercentage === 100;

  // Notify parent component when configuration status changes
  useEffect(() => {
    if (onConfigurationChange) {
      onConfigurationChange(isConfigured);
    }
  }, [isConfigured, onConfigurationChange]);

  // Update app onboarding data when configuration changes
  useEffect(() => {
    if (isConfigured && relatedRepos && relatedRepos.length > 0) {
      setAppOnboardingData(prevData => ({
        ...prevData,
        onboarding_details: {
          ...prevData.onboarding_details,
          github_repos: relatedRepos.map(repo => repo.nameWithOwner || repo.name).join(', '),
          msa_certification_status: msaCertificationStatus,
          msa_certification_percentage: msaCertificationPercentage,
          exists_in_snow_status: 'Yes',
          approval_group: [],
          support_group: []
        }
      }));
    }
  }, [isConfigured, relatedRepos, msaCertificationStatus, msaCertificationPercentage, setAppOnboardingData]);

  return (
    <Card sx={{
      border: '1px solid #d9d9d9', 
      boxShadow: 'none',
      borderRadius: '10px' 
    }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <McIcon icon="lightning"/>
          <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>
            Step 3B: Automated Change Process (Green Channel) - Integration Setup
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: maerskFontFamily }}>
          Configure GitHub repositories to automatically track CI/CD deployments through Flux for change traceability
        </Typography>

        {/* Repository Integration Section */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <McIcon icon="link"/>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
              Repository Integration
            </Typography>
          </Box>

          <div>
            {filteredData === null ? (
              <div>
                <Skeleton height={40} width="100%" style={{ marginBottom: "16px" }} />
                <Skeleton height={"40vh"} />
              </div>
            ) : (
              <>
                <div>
                  <McModal
                    zindex={99999}
                    body=""
                    fit="small"
                    open={isDeleteModalOpened}
                    closed={() => {
                      setIsDeleteModalOpened(false);
                      setSelectedApplicationInstance('');
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "20px",
                        gap: "16px"
                      }}
                    >
                      {/* <div style={{ textAlign: "center" }}>
                        {`Are you sure you want to ${
                          activeTab === "To suggested_repositories" ? "tag" : "untag"
                        } the selected rows?`}
                      </div> */}
                      
                      {/* Show application instance selection only when tagging from suggested repositories */}
                      {activeTab === "To suggested_repositories" && (
                        <div style={{ width: "100%", marginTop: "16px" }}>
                          <p style={{ 
                            marginBottom: "8px",
                            fontWeight: "600",
                            fontSize: "14px",
                            fontFamily: maerskFontFamily,
                            textAlign: "left"
                          }}>
                            Select Application Instance you want to tag selected repository(ies) to:
                            <span style={{ color: "red", marginLeft: "3px" }}>*</span>
                          </p>
                          <McSelect
                            label=""
                            placeholder="Choose application instance"
                            value={selectedApplicationInstance}
                            optionselected={(e) => {
                              setSelectedApplicationInstance(e.detail.value);
                            }}
                            fit="medium"
                            style={{ width: '100%' }}
                          >
                            {appOnboardingData?.onboarding_details?.application_instances && 
                             Array.isArray(appOnboardingData.onboarding_details.application_instances) ? (
                              appOnboardingData.onboarding_details.application_instances.map((instanceName, index) => (
                                <McOption key={index} value={instanceName}>
                                  {instanceName}
                                </McOption>
                              ))
                            ) : (
                              appOnboardingData?.application_name && (
                                <McOption value={appOnboardingData.application_name}>
                                  {appOnboardingData.application_name}
                                </McOption>
                              )
                            )}
                          </McSelect>
                        </div>
                      )}
                    </div>

                    <McButton
                      slot="primaryAction"
                      appearance="primary"
                      fit="medium"
                      loading={loading}
                      disabled={activeTab === "To suggested_repositories" && !selectedApplicationInstance}
                      click={() => {
                        activeTab === "To suggested_repositories" ? handleCertify(selectedRows) : handleDelete(selectedRows);
                      }}
                    >
                      Save your changes
                    </McButton>
                    <McButton slot="secondaryAction" appearance="neutral" variant="filled" dialogaction="cancel" fit="medium">
                      Cancel
                    </McButton>
                  </McModal>

                  {/* <McModal
                    zindex={99999}
                    body=""
                    fit="medium"
                    height={'480px'}
                    open={isGatekeeperModalOpened}
                    closed={() => setIsGatekeeperModalOpened(false)}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "20px",
                        gap: "16px"
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontFamily: maerskFontFamily }}>
                          Gatekeeper Integration Instructions
                        </Typography>
                        <McButton
                          appearance="primary"
                          variant="outlined"
                          fit="small"
                          icon="square-arrow-up-right"
                          click={() => {
                            window.open("https://maersk-tools.atlassian.net/wiki/spaces/SPE/pages/183942513835/Gatekeeper+Solution+for+GitHub+Actions+Controlled+Production+Deployments", "_blank");
                          }}
                        >
                          View Documentation
                        </McButton>
                      </Box>
                      
                      <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                        Follow the detailed instructions in our documentation and copy the below Code Snippet for GitHub Workflow:
                      </Typography>
                      
                      <Box sx={{ position: 'relative' }}>
                        <McButton
                          appearance="neutral"
                          variant="filled"
                          fit="small"
                          icon="copy"
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            zIndex: 1
                          }}
                          click={() => {
                            const codeSnippet = `name: App Stage Deploy

on:
  workflow_dispatch:
    inputs:
      incident_number:
        description: "Optional: Break-glass Incident Number (e.g., INC12345). Provide with justification to bypass approvals."
        required: false
        type: string
      justification:
        description: "Optional: Business justification (required if using incident_number to break-glass)."
        required: false
        type: string

permissions:
  contents: read
  deployments: write

jobs:
  use-shared-approval:
    uses: Maersk-Global/sre-network-tools-common/.github/workflows/prod-approval.yml@main
    with:
      environment: prod
      caller_repo: \${{ github.repository }}
      caller_run_url: \${{ github.server_url }}/\${{ github.repository }}/actions/runs/\${{ github.run_id }}
      # 👇 Pass through break-glass fields
      incident_number: \${{ github.event.inputs.incident_number }}
      justification:   \${{ github.event.inputs.justification }}
    secrets:
      GATEKEEPER_CLIENT_ID: \${{ secrets.GATEKEEPER_CLIENT_ID }}
      GATEKEEPER_CLIENT_SECRET: \${{ secrets.GATEKEEPER_CLIENT_SECRET }}`;
                            navigator.clipboard.writeText(codeSnippet);
                            toast.success("Code snippet copied to clipboard", {
                              position: "top-center",
                              autoClose: 3000,
                              hideProgressBar: false,
                              closeOnClick: true,
                              pauseOnHover: true,
                              draggable: true,
                              progress: undefined,
                              theme: "light"
                            });
                          }}
                        >
                          Copy Code
                        </McButton>
                        
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            padding: 2,
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            whiteSpace: 'pre-wrap',
                            overflow: 'auto',
                            maxHeight: '300px',
                            paddingTop: '40px' // Add padding to accommodate the copy button
                          }}
                        >
{`name: App Stage Deploy

on:
  workflow_dispatch:
    inputs:
      incident_number:
        description: "Optional: Break-glass Incident Number (e.g., INC12345). Provide with justification to bypass approvals."
        required: false
        type: string
      justification:
        description: "Optional: Business justification (required if using incident_number to break-glass)."
        required: false
        type: string

permissions:
  contents: read
  deployments: write

jobs:
  use-shared-approval:
    uses: Maersk-Global/sre-network-tools-common/.github/workflows/prod-approval.yml@main
    with:
      environment: prod
      caller_repo: \${{ github.repository }}
      caller_run_url: \${{ github.server_url }}/\${{ github.repository }}/actions/runs/\${{ github.run_id }}
      # 👇 Pass through break-glass fields
      incident_number: \${{ github.event.inputs.incident_number }}
      justification:   \${{ github.event.inputs.justification }}
    secrets:
      GATEKEEPER_CLIENT_ID: \${{ secrets.GATEKEEPER_CLIENT_ID }}
      GATEKEEPER_CLIENT_SECRET: \${{ secrets.GATEKEEPER_CLIENT_SECRET }}`}
                        </Box>
                      </Box>
                    </div>
                  </McModal> */}

                  <McModal
                    zindex={99999}
                    body=""
                    fit="small"
                    open={isNewRelationModalOpened}
                    closed={() => {
                      setServerName("");
                      setIsNewRelationModalOpened(false);
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "20px",
                        gap: "16px"
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px"
                        }}
                      >
                        <McInput label="Application name" disabled name="username" value={appOnboardingData?.application_name}></McInput>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px"
                        }}
                      >
                        <McInput
                          label="Server Name"
                          placeholder="Name of the server to relate to"
                          hint="Add up to 10 servers separated by a comma"
                          clearbutton
                          value={serverName}
                          input={handleServerNameChange}
                          clearbuttonclick={handleClearButtonClick}
                        ></McInput>
                      </div>
                    </div>

                    <McButton
                      slot="primaryAction"
                      appearance="primary"
                      fit="medium"
                      dialogaction="ok"
                    >
                      Save
                    </McButton>
                    <McButton slot="secondaryAction" appearance="neutral" variant="filled" dialogaction="cancel" fit="medium">
                      Cancel
                    </McButton>
                  </McModal>

                  <McTabBar
                    className="repo-tab-bar"
                    tabchange={(e) => handleTabChange(e)}
                    currentindex={activeTab === "To related_repositories" ? 0 : 1}
                  >
                    <McTab
                      slot="tab"
                      label="Related Repositories"
                      active={activeTab === "To related_repositories"}
                    ></McTab>
                    <div slot="panel">
                      <div
                        className="filters-and-actions"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "10px"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          {/* <div className="filter-button-container" style={{ display: "flex", gap: "8px" }}>
                            <McButton
                              label={activeFilters.length > 0 ? `Filters (${activeFilters.length})` : "Filters"}
                              icon="sliders-horizontal"
                              appearance={activeFilters.length > 0 ? "primary" : "neutral"}
                              variant="plain"
                              click={() => setIsFilterMenuOpened(!isFilterMenuOpened)}
                            >
                              {activeFilters.length > 0 && (
                                <McBadge
                                  display="pinned"
                                  slot="badge"
                                  label={activeFilters.length}
                                  max={99}
                                  appearance="info"
                                  position="top"
                                ></McBadge>
                              )}
                            </McButton>

                            <div className={`filter-dropdown ${isFilterMenuOpened ? "open" : ""}`}>
                              <FilterMenu
                                columns={currentColumns}
                                operators={[
                                  { value: "contains", label: "Contains" },
                                  { value: "Does not contain", label: "Does not contain" },
                                  { value: "equals", label: "Equals" },
                                  { value: "Is not", label: "Is not" },
                                  { value: "startsWith", label: "Starts with" },
                                  { value: "endsWith", label: "Ends with" },
                                  { value: "is one of", label: "Is one of" },
                                  { value: "is not one of", label: "Is not one of" }
                                ]}
                                data={combinedData}
                                label={activeTab}
                                onApply={(conditions) => {
                                  console.log("Applied conditions:", conditions);
                                  setActiveFilters(conditions);
                                  setIsFilterMenuOpened(false);
                                }}
                                onCancel={() => setIsFilterMenuOpened(false)}
                                setIsMenuOpen={setIsFilterMenuOpened}
                                setRelationData={(data) => {
                                  if (activeTab === "To related_repositories") {
                                    setRelatedRepos(data[activeTab]);
                                  } else {
                                    setSuggestedRepos(data[activeTab]);
                                  }
                                }}
                                setActiveFilters={setActiveFilters}
                              ></FilterMenu>
                            </div>
                          </div> */}
                          {/* Selection disabled for related repositories */}
                          {false && (
                            <div
                              className="selected-div"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#E9F6FC",
                                borderRadius: "8px",
                                padding: "12px",
                                gap: "8px"
                              }}
                            >
                              {selectedRows.length} selected
                              <McButton
                                label={activeTab === "To suggested_repositories" ? "Tag" : "Untag"}
                                fit="small"
                                appearance="neutral"
                                click={() => {
                                  setIsDeleteModalOpened(true);
                                }}
                              ></McButton>
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <div>
                            {activeFilters.length > 0
                              ? `Filtered: ${filteredData.length} / Total: ${combinedData.length}`
                              : `Total: ${combinedData.length}`}
                          </div>
                          <McMenu trigger="click" position="bottom-center" arrow={true} zindex={99999}>
                            <McButton
                              slot="trigger"
                              icon="stack"
                              trailingicon="chevron-down"
                              variant="outlined"
                              appearance="neutral"
                            >
                              Actions
                            </McButton>
                            <McList>
                              <McListItem label="Download" onClick={handleDownload}></McListItem>
                            </McList>
                          </McMenu>
                        </div>
                      </div>

                      <div
                        className="table-container"
                        style={{
                          marginTop: "8px",
                          height: "auto"
                        }}
                      >
                        <McTable
                          data={filteredData}
                          columns={currentColumns}
                          customstyles={tableStyles}
                          datakey="id"
                          expand
                          headersticky
                          slot="loading"
                          fit="medium"
                          stateslotheight="400px"
                          select={false}
                          rowselected={handleRowSelected}
                          rowclick={(e) => {
                            console.log("Row clicked", e);
                          }}
                          selectalllabel="Select all rows"
                          selectlabel="Select row"
                          selected={selectedRows}
                          selectchange={(e) => handleSelectChange(e)}
                          hidecolumns={
                            activeTab === "To suggested_repositories" ? ["ApplicationId", "ApplicationName", "status"] : []
                          }
                          sortdefaultdirection="ascending"
                        >
                          {isLoading && <McLoadingIndicator slot="state" appearance="primary"/>}
                          {!isLoading && filteredData?.length === 0 && <div slot="state">No data found</div>}

                          {!isLoading && filteredData?.map((row, index) => {
                            return (
                              <>
                                <div
                                  style={{
                                    background: "inherit",
                                    textAlign: "left"
                                  }}
                                  className="mds"
                                  slot={`${row.id}_url`}
                                >
                                  <McIcon
                                    icon="square-arrow-up-right"
                                    color="var(--mds_brand_appearance_secondary_default_text-color)"
                                    slot={`${row.id}_url`}
                                    style={{
                                      cursor: "pointer"
                                    }}
                                    onClick={(e) => {
                                      window.open(row.url, "_blank");
                                    }}
                                  >
                                  </McIcon>
                                </div>
                                <div slot={`${row.id}_nameWithOwner`}>
                                  {row.nameWithOwner?.replace("Maersk-Global/", "") || row.nameWithOwner}
                                </div>
                                <div slot={`${row.id}_status`}>
                                  <McBadge display="inline" label={row.status} appearance="success"></McBadge>
                                </div>
                              </>
                            );
                          })}
                        </McTable>
                      </div>
                    </div>

                    {/* <McTab
                      slot="tab"
                      label="Add New Repositories"
                      active={activeTab === "To suggested_repositories"}
                    ></McTab>
                    <div slot="panel">
                      <div
                        className="filters-and-actions"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "10px"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          {selectedRows.length > 0 && (
                            <div
                              className="selected-div"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor: "#E9F6FC",
                                borderRadius: "8px",
                                padding: "12px",
                                gap: "8px"
                              }}
                            >
                              {selectedRows.length} selected
                              <McButton
                                label={activeTab === "To suggested_repositories" ? "Tag" : "Untag"}
                                fit="small"
                                appearance="neutral"
                                click={() => {
                                  setIsDeleteModalOpened(true);
                                }}
                              ></McButton>
                            </div>
                          )}
                        </div>
                        {activeTab == "To suggested_repositories" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              gap: "8px"
                            }}
                          >
                            <McTypeahead
                              style={{ width: "300px" }}
                              ref={typeaheadref}
                              hiddenlabel={true}
                              value={repoSearch}
                              maxoptions="20"
                              data={options}
                              hint="Search for a repo if not found in the below table"
                              name="username"
                              clearbutton={true}
                              input={(e) => setRepoSearch(e.target.value)}
                              focus={(e) => console.log("Focus event:", e)}
                              blur={(e) => console.log("Blur event:", e)}
                              click={(e) => console.log("Click event:", e)}
                              keypress={(e) => console.log("KeyPress event:", e.key)}
                              clearbuttonclick={(e) => {
                                console.log("ClearButtonClick event:", e);
                                setOptions(null);
                                setRepoSearch("");
                                if (activeTab === "To related_repositories") {
                                  setRelatedRepos(null);
                                } else {
                                  setSuggestedRepos(null);
                                }
                                fetchRepositoryData(activeTab, appOnboardingData?.application_id, appOnboardingData?.onboarding_details?.application_instances);
                              }}
                              trailingiconclick={(e) => console.log("TrailingIconClick event:", e)}
                              search={async (e) => {
                                setOptions(null);
                                typeaheadref.current.innerHTML = "";
                                typeaheadref.current.loading = true;

                                await handleSearch(e);
                                typeaheadref.current.loading = false;
                              }}
                              optionselected={handleOptionSelected}
                              optionsheight="200px"
                              keepclearbuttonvisible
                              showlistonfocus={true}
                              minchars="3"
                              debounce="500"
                              placeholder="Type a min of 3 characters to search"
                            ></McTypeahead>
                            <McButton
                              label="Search"
                              icon="magnifying-glass"
                              appearance="primary"
                              click={(e) => {
                                console.log("Search clicked");
                                handleRepoNameSearch(e);
                              }}
                            ></McButton>
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <div>
                            {activeFilters.length > 0
                              ? `Filtered: ${filteredData.length} / Total: ${combinedData.length}`
                              : `Total: ${combinedData.length}`}
                          </div>
                          <McMenu trigger="click" position="bottom-center" arrow={true} zindex={99999}>
                            <McButton
                              slot="trigger"
                              icon="stack"
                              trailingicon="chevron-down"
                              variant="outlined"
                              appearance="neutral"
                            >
                              Actions
                            </McButton>
                            <McList>
                              <McListItem label="Download" onClick={handleDownload}></McListItem>
                            </McList>
                          </McMenu>
                        </div>
                      </div>

                      <div
                        className="table-container"
                        style={{
                          marginTop: "8px"
                        }}
                      >
                        <McTable
                          data={filteredData}
                          columns={currentColumns}
                          customstyles={tableStyles}
                          datakey="id"
                          expand
                          headersticky
                          slot="loading"
                          fit="medium"
                          stateslotheight="400px"
                          select={true}
                          rowselected={handleRowSelected}
                          rowclick={(e) => {
                            console.log("Row clicked", e);
                          }}
                          selectalllabel="Select all rows"
                          selectlabel="Select row"
                          selected={selectedRows}
                          selectchange={(e) => handleSelectChange(e)}
                          hidecolumns={
                            activeTab === "To suggested_repositories" ? ["ApplicationId", "ApplicationName", "status"] : []
                          }
                          sortdefaultdirection="ascending"
                        >
                          {isLoading && <McLoadingIndicator slot="state" appearance="primary"/>}
                          {!isLoading && filteredData?.length === 0 && <div slot="state">No data found</div>}

                          {!isLoading && filteredData?.map((row, index) => {
                            return (
                              <>
                                <div
                                  style={{
                                    background: "inherit",
                                    textAlign: "left"
                                  }}
                                  className="mds"
                                  slot={`${row.id}_url`}
                                >
                                  <McIcon
                                    icon="square-arrow-up-right"
                                    color="var(--mds_brand_appearance_secondary_default_text-color)"
                                    slot={`${row.id}_url`}
                                    style={{
                                      cursor: "pointer"
                                    }}
                                    onClick={(e) => {
                                      console.log("Icon clicked", e);
                                      window.open(row.url, "_blank");
                                    }}
                                  >
                                  </McIcon>
                                </div>
                                <div slot={`${row.id}_nameWithOwner`}>
                                  {row.nameWithOwner?.replace("Maersk-Global/", "") || row.nameWithOwner}
                                </div>
                                {activeTab === "To suggested_repositories" && (
                                  <>
                                    <div slot={`${row.id}_match_type`}>
                                      <McBadge 
                                        display="inline" 
                                        label={row.match_type || 'N/A'} 
                                        appearance={row.match_type === 'exact' ? 'success' : row.match_type === 'partial' ? 'warning' : 'neutral'}
                                      />
                                    </div>
                                  </>
                                )}
                                <div slot={`${row.id}_status`}>
                                  <McBadge display="inline" label={row.status} appearance="success"></McBadge>
                                </div>
                              </>
                            );
                          })}
                        </McTable>
                      </div>
                    </div> */}
                  </McTabBar>
                </div>
              </>
            )}
          </div>
        </Box>

        {/* MSA Certification Section */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <McIcon icon="shield-check"/>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
              MSA Certification
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
            Ensure MSA Certification is complete for all production instances of the Application
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <McButton
                appearance="primary"
                // variant="outlined"
                fit="medium"
                icon="play"
                disabled={!relatedRepos || relatedRepos.length === 0}
                loading={isMsaCertificationLoading}
                click={handleRunMsaCertificationCheck}
              >
                Run MSA Certification Check
              </McButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  Certification Check Status:
                </Typography>
                <McTag
                  appearance={
                    msaCertificationStatus === 'pass' ? 'success' : 
                    msaCertificationStatus === 'fail' ? 'error' : 'warning'
                  }
                  label={
                    msaCertificationStatus === 'pass' ? 'Pass' :
                    msaCertificationStatus === 'fail' ? 'Fail' : 'Pending'
                  }
                  size="small"
                />
                {msaCertificationPercentage > 0 && (
                  <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                    ({msaCertificationPercentage}%)
                  </Typography>
                )}
              </Box>
              {msaCertificationMessage && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic', fontFamily: maerskFontFamily }}>
                    {msaCertificationMessage}.
                  </Typography>
                  {msaCertificationPercentage !== 100 && (
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontFamily: maerskFontFamily }}>
                      <a 
                        href={`${process.env.REACT_APP_MSA_LINK}asset_lookup?applicationId=${appOnboardingData?.application_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          color: 'var(--mds_brand_appearance_primary_default_text-color)', 
                          textDecoration: 'underline',
                          fontFamily: maerskFontFamily
                        }}
                      >
                        Click here to complete certification
                      </a>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Gatekeeper Integration Section */}
        {/* <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <McIcon icon="shield-check"/>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
              Gatekeeper Integration
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: maerskFontFamily }}>
            Ensure Gatekeeper is integrated in your GitHub Workflows to complete Repository integration
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <McButton
                appearance="primary"
                fit="medium"
                icon="plus"
                click={handleGatekeeperIntegration}
              >
                Integrate Gatekeeper
              </McButton>

              <McButton
                appearance="neutral"
                variant="outlined"
                fit="medium"
                icon="play"
                disabled={!relatedRepos || relatedRepos.length === 0}
                loading={isGatekeeperLoading}
                click={handleRunGatekeeperCheck}
              >
                Run Gatekeeper Integration Check
              </McButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                  Gatekeeper Integration Status:
                </Typography>
                <McTag
                  appearance={
                    gatekeeperStatus === 'pass' ? 'success' : 
                    gatekeeperStatus === 'fail' ? 'error' : 'warning'
                  }
                  label={
                    gatekeeperStatus === 'pass' ? 'Pass' :
                    gatekeeperStatus === 'fail' ? 'Fail' : 'Pending'
                  }
                  size="small"
                />
              </Box>
              {gatekeeperMessage && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontStyle: 'italic', fontFamily: maerskFontFamily }}>
                  {gatekeeperMessage}
                </Typography>
              )}
            </Box>
          </Box>
        </Box> */}

        {/* Green Change Beacon Status */}
        <Alert 
          severity={isConfigured ? 'success' : 'warning'} 
          sx={{ borderRadius: '10px' }}
        >
          <Typography variant="subtitle2" sx={{ fontFamily: maerskFontFamily }}>
            Green Change Beacon Status
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, fontFamily: maerskFontFamily }}>
            {isConfigured ? 'ENABLED' : 'PENDING'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontFamily: maerskFontFamily }}>
            {isConfigured 
              ? `- ${relatedRepos?.length} GitHub repositories mapped and Application Certification in MSA complete (100%). Green Change Beacon is enabled for rapid deployment approvals.`
              : relatedRepos && relatedRepos.length > 0
              ? `- Complete Application Certification in MSA to enable the Green Change Beacon for rapid deployment approvals.`
              : `- Map GitHub repositories to the application and complete certification in MSA to enable the Green Change Beacon for rapid deployment approvals.`
            }
          </Typography>
          {relatedRepos && relatedRepos.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                Mapped Repositories:
              </Typography>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {relatedRepos.slice(0, 3).map((repo, index) => (
                  <li key={index}>
                    <Typography variant="caption" sx={{ fontFamily: maerskFontFamily }}>
                      {repo.nameWithOwner || repo.name || 'Unknown Repository'}
                    </Typography>
                  </li>
                ))}
                {relatedRepos.length > 3 && (
                  <li>
                    <Typography variant="caption" sx={{ fontFamily: maerskFontFamily }}>
                      ... and {relatedRepos.length - 3} more
                    </Typography>
                  </li>
                )}
              </ul>
            </Box>
          )}
          {msaCertificationMessage && msaCertificationStatus !== 'pending' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: maerskFontFamily }}>
                MSA Certification Status:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: maerskFontFamily, display: 'block', mt: 0.5 }}>
                {msaCertificationMessage}
              </Typography>
            </Box>
          )}
        </Alert>
      </CardContent>
    </Card>
  );
};

export default RapidHealthCheck;
